import { moodboardFramePiece } from '@shared/moodboard/frame'
import type { CanvasNode } from '@shared/services/canvasService'
import type { SentMoodboard } from '@shared/moodboard/send'
import { decodeRef } from '@shared/services/imageRefs'
import { bitmapOf, cutForBoard } from '../pages/moodboardRender'
import { renderFloat } from '@shared/moodboard/float'
import { shownWhole, type BoardPicture, type Sheet } from './MoodboardBoard'
import { isTurned } from '@shared/moodboard/turn'

/** Where a cut picture is kept: a project's file, by id and link. */
export type KeepFile = (blob: Blob, name: string, size: { width: number; height: number }) => Promise<{ id: string; url: string }>

export interface CutFrame {
  title: string
  /** The frame and everything in it, as nodes for a project's board (frame.ts). */
  piece: CanvasNode[]
  /** Every file the nodes use: the cut pictures, then the colour swatches. */
  files: string[]
  /** The cut pictures, for a clipboard's preview. */
  items: { kind: 'file'; id: string; thumb: string; title: string }[]
}

/**
 * A mood board as a FRAME on a project's board (Huanrui, 2026-10-12; shared
 * 2026-10-16 by the Mood board page's Copy to project and the canvas's Mood
 * board card): every picture cut as the sheet shows it and kept with `keep`,
 * the colour strip as small colour pictures, all at the sheet's own
 * 1920 x 1080 (frame.ts moodboardFramePiece). Four pictures are cut at once.
 */
export async function cutMoodboardFrame(
  board: SentMoodboard,
  sheet: Sheet,
  pictures: BoardPicture[],
  keep: KeepFile,
  onProgress?: (done: number, total: number) => void,
): Promise<CutFrame> {
  /*
   * ORIGINALS (Huanrui, 2026-10-16). A picture shown whole is not copied at
   * all: the frame shows the library picture or the board's own file itself.
   * A picture the sheet cuts is cut from the original at full size -- every
   * pixel of the part shown, nothing scaled down -- and kept.
   */
  const files: string[] = new Array(sheet.slots.length).fill('')
  const assets: string[] = new Array(sheet.slots.length).fill('')
  const thumbs: string[] = new Array(sheet.slots.length).fill('')
  const queue: { slot: Sheet['slots'][number]; n: number }[] = []
  sheet.slots.forEach((slot, n) => {
    const src = pictures[slot.pic].src
    const own = src.asset ? { store: 'library' as const, id: src.asset.id } : decodeRef(src.ref)
    // A translucent ground is cut even when shown whole, and so made solid (2026-10-23).
    if (own && shownWhole(slot.crop) && !isTurned(slot.turn) && pictures[slot.pic].clear !== 'semi') {
      if (own.store === 'library') assets[n] = own.id
      else files[n] = own.id
      thumbs[n] = src.thumb
    } else queue.push({ slot, n })
  })
  const cutting = queue.length
  let done = 0
  onProgress?.(0, cutting)
  const worker = async () => {
    for (let next = queue.shift(); next; next = queue.shift()) {
      const { slot, n } = next
      const src = pictures[slot.pic].src
      const blob = await cutForBoard({ url: src.full || src.url, x: slot.x, y: slot.y, w: slot.w, h: slot.h, crop: slot.crop, turn: slot.turn }, true)
      const bitmap = await createImageBitmap(blob)
      // A cut-out stays a PNG (2026-10-23), and is named so.
      const stored = await keep(blob, `${(src.title || 'picture').slice(0, 60)}.${blob.type === 'image/png' ? 'png' : 'jpg'}`, {
        width: bitmap.width,
        height: bitmap.height,
      })
      bitmap.close()
      files[n] = stored.id
      thumbs[n] = stored.url
      done += 1
      onProgress?.(done, cutting)
    }
  }
  await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker))

  // Lifted pictures (2026-10-23): each drawn whole with its outline and turn
  // (float.ts) as one see-through PNG, kept, and laid over the rest.
  const floated = await Promise.all(
    sheet.floats.map(async (one) => {
      const src = pictures[one.pic].src
      const image = await bitmapOf(src.full || src.url)
      try {
        const blob = await renderFloat(image, one.spec)
        const drawn = await createImageBitmap(blob)
        const stored = await keep(blob, `${(src.title || 'picture').slice(0, 60)}.png`, { width: drawn.width, height: drawn.height })
        drawn.close()
        return { one, stored }
      } finally {
        image.close()
      }
    }),
  )

  // The colour strip: one small picture of each colour.
  const swatchFiles = await Promise.all(
    board.swatches.map(async (swatch) => {
      const canvas = document.createElement('canvas')
      canvas.width = 64
      canvas.height = 64
      const pen = canvas.getContext('2d')!
      pen.fillStyle = swatch.color
      pen.fillRect(0, 0, 64, 64)
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((out) => (out ? resolve(out) : reject(new Error('Could not make a colour'))), 'image/png'),
      )
      return (await keep(blob, `${swatch.color}.png`, { width: 64, height: 64 })).id
    }),
  )

  const piece = moodboardFramePiece({
    title: board.title,
    ground: board.background,
    pictures: sheet.slots.map((slot, n) => ({
      ...(assets[n] ? { assetId: assets[n] } : { fileId: files[n] }),
      title: pictures[slot.pic].src.title,
      x: slot.x,
      y: slot.y,
      w: slot.w,
      h: slot.h,
    })).concat(
      floated.map(({ one, stored }) => ({
        fileId: stored.id,
        title: pictures[one.pic].src.title,
        ...one.box,
        float: true,
      })),
    ),
    texts: board.texts,
    swatches: board.swatches.map((swatch, i) => ({ ...swatch, fileId: swatchFiles[i] })),
  })
  return {
    title: board.title,
    piece,
    // The project files the frame uses: cut ones, ones already on the board, swatches.
    files: [...files.filter(Boolean), ...floated.map(({ stored }) => stored.id), ...swatchFiles],
    items: sheet.slots
      .map((slot, n) => ({
        kind: 'file' as const,
        id: files[n] || assets[n],
        thumb: thumbs[n],
        title: pictures[slot.pic].src.title,
      }))
      .concat(floated.map(({ one, stored }) => ({ kind: 'file' as const, id: stored.id, thumb: stored.url, title: pictures[one.pic].src.title }))),
  }
}
